import React from "react";

import { cn } from "../lib/utils";

/**
 * Tiny traffic-light dot for surfacing a single signal source's health
 * (Pep / Plug) without changing the box's overall State classification.
 *
 * Four colours:
 *   - "ok" → green (peplink online, plug on, UPS on shore power, etc.)
 *   - "warn" → amber (degraded but not down — UPS on battery, holding)
 *   - "bad" → red (peplink offline, plug off, UPS low/shutdown)
 *   - "unknown" → gray (mapped but no clear evidence — comms lost)
 *
 * `null` status → render nothing (the SCU has no mapping for this source).
 *
 * The component renders inline as `[label] ● [optional inline]`. The
 * tooltip carries the full timestamp + reasoning. Designed for table
 * cells where horizontal space is tight.
 */
export type DotStatus = "ok" | "warn" | "bad" | "unknown";

const DOT_BG: Record<DotStatus, string> = {
  ok: "bg-success-500",
  warn: "bg-warning-500",
  bad: "bg-error-500",
  unknown: "bg-secondary-400",
};

const TEXT_COLOR: Record<DotStatus, string> = {
  ok: "text-success-700",
  warn: "text-warning-700",
  bad: "text-error-700",
  unknown: "text-secondary-500",
};

interface SignalDotProps {
  /** Short label rendered before the dot ("Pep" / "Plug"). */
  label: string;
  /** null → don't render at all (SCU has no mapping for this source). */
  status: DotStatus | null;
  /** Optional one-line addendum after the dot, e.g. "online 5h" / "off 30m". */
  inline?: string | null;
  /** Tooltip — the longer story that the inline can't fit. */
  title?: string;
  className?: string;
}

export const SignalDot: React.FC<SignalDotProps> = ({
  label,
  status,
  inline,
  title,
  className,
}) => {
  if (status === null) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-xs whitespace-nowrap",
        TEXT_COLOR[status],
        className,
      )}
      title={title}
    >
      <span className="text-secondary-600">{label}</span>
      <span
        aria-hidden="true"
        className={cn("inline-block h-2 w-2 rounded-full", DOT_BG[status])}
      />
      {inline ? <span>{inline}</span> : null}
    </span>
  );
};

export default SignalDot;
